/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : FilterTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CustomCheckBoxV2 from "../checkBox/CustomCheckBoxV2";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

function FilterTemplate({ data, handleOnChange, defaultValue }) {
  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    if (!defaultValue || defaultValue.length === 0) {
      const allValues = data.map((item) => item.value);
      setSelectedValues(allValues);
      handleOnChange(data);
    } else {
      const defaultValueValues = defaultValue.map((item) => item.value).sort();
      const dataValues = data.map((item) => item.value).sort();
      const isSameAsData =
        JSON.stringify(defaultValueValues) === JSON.stringify(dataValues);

      if (isSameAsData) {
        const allValues = data.map((item) => item.value);
        setSelectedValues(allValues);
      } else {
        const defaultValues = defaultValue.map((item) => item.value);
        setSelectedValues(defaultValues);
      }
    }
  }, [defaultValue, data]);

  const handleCheckboxChange = (value, checked) => {
    if (value === "ALL") {
      if (checked) {
        const allValues = data.map((item) => item.value);
        setSelectedValues(allValues);
        handleOnChange(data);
      } else {
        toast.error("Atleast one field should be selected");
      }
    } else {
      let newSelectedValues;
      if (checked) {
        newSelectedValues = [...selectedValues, value];
      } else {
        newSelectedValues = selectedValues.filter((v) => v !== value);
        if (newSelectedValues.length === 0) {
          toast.error("Atleast one field should be selected");
          return;
        }
      }

      setSelectedValues(newSelectedValues);
      const selectedData = data.filter((item) =>
        newSelectedValues.includes(item.value)
      );
      handleOnChange(selectedData);
    }
  };

  const isAllSelected = selectedValues.length === data.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
      {[{ value: "ALL", label: "All" }, ...data].map((item) => {
        const checked =
          item.value === "ALL"
            ? isAllSelected
            : selectedValues.includes(item.value);

        return (
          <CustomCheckBoxV2
            key={item.value}
            label={item.label}
            value={checked}
            onChange={(e) => {
              handleCheckboxChange(item.value, e.target.checked);
            }}
          />
        );
      })}
    </Box>
  );
}

FilterTemplate.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.node,
    })
  ),
  handleOnChange: PropTypes.func,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.node,
    })
  ),
};

FilterTemplate.defaultProps = {
  data: [],
  handleOnChange: () => {},
  defaultValue: [],
};

export default FilterTemplate;
